import { ReactNode, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useIsDarkTheme, useTheme } from '../../hooks/useTheme';
import { AccountMenu } from './AccountMenu';
import { FaqModal } from '../FaqModal';
import { SupportModal } from '../SupportModal';
import { FeedbackPopover } from '../FeedbackPopover';
import { NotificationsPopover } from './NotificationsPopover';
import { IconButton, NavItem, Sidebar } from '../design-system';
import type { SidebarSection } from '../design-system';

/**
 * AppSidebarShell — the app-specific adapter around the design-system Sidebar.
 * Owns everything the DS rail shouldn't know about: theme toggle, the
 * FAQ/Support/Notifications/Feedback utilities, the account menu, and logo
 * navigation. AppSidebar, TripLayout and HostMatchLayout all render through
 * this shape of shell.
 */
export interface AppSidebarShellProps {
  open: boolean;
  onClose: () => void;
  logoHref: string;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  /** Grouped nav rendered by the DS Sidebar. */
  sections?: SidebarSection[];
  /** Key of the active nav item (matched against item.key). */
  active?: string;
  /** Custom block under the logo (e.g. trip switcher / host profile card). */
  headerSlot?: ReactNode;
  /** Extra blocks rendered in the nav column after sections. */
  children?: ReactNode;
}

function UtilityIcon({ d }: { d: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 shrink-0">
      <path fillRule="evenodd" clipRule="evenodd" d={d} />
    </svg>
  );
}

export function AppSidebarShell({
  open,
  onClose,
  logoHref,
  collapsed,
  onToggleCollapse,
  sections,
  active,
  headerSlot,
  children,
}: AppSidebarShellProps) {
  const navigate = useNavigate();
  const { activeRole } = useAuth();
  // `toggle` is a stateless action — safe to call from any useTheme()
  // instance. The icon it renders, though, must NOT read this instance's
  // own `theme` state: AccountMenu (footerSlot below) toggles the theme
  // through its own separate useTheme() call, which this instance never
  // hears about — see the staleness note on useIsDarkTheme in useTheme.ts.
  const { toggle: toggleTheme } = useTheme();
  const isDarkTheme = useIsDarkTheme();
  const [showFaq, setShowFaq] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const c = collapsed ?? false;

  const utilityLabel = (text: string) => (
    <span className={c ? 'md:hidden' : undefined}>{text}</span>
  );

  return (
    <>
      <Sidebar
        open={open}
        onClose={onClose}
        collapsed={collapsed}
        onToggleCollapse={onToggleCollapse}
        onLogoClick={() => { navigate(logoHref); onClose(); }}
        headerActions={<IconButton icon={isDarkTheme ? 'sun' : 'moon'} label="Toggle theme" onClick={toggleTheme} />}
        sections={sections}
        active={active}
        headerSlot={headerSlot}
        footerSlot={<AccountMenu variant={c ? 'default' : 'quiet'} collapsed={c} onNavigate={onClose} />}
      >
        {children}

        {/* Bottom utilities — always visible above the account menu */}
        <div ref={anchorRef} className="mt-auto pt-4 flex flex-col gap-0.5" style={{ borderTop: '1px solid var(--line)' }}>
          <NavItem
            size="sm"
            onClick={() => setShowFaq(true)}
            title={c ? 'FAQ' : undefined}
            className={c ? 'md:justify-center' : undefined}
            icon={<UtilityIcon d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM8.94 6.94a.75.75 0 11-1.061-1.061 3 3 0 112.871 5.026v.345a.75.75 0 01-1.5 0v-.5c0-.72.57-1.172 1.081-1.287A1.5 1.5 0 108.94 6.94zM10 15a1 1 0 100-2 1 1 0 000 2z" />}
            label={utilityLabel('FAQ')}
          />
          <NavItem
            size="sm"
            onClick={() => setShowSupport(true)}
            title={c ? 'Support' : undefined}
            className={c ? 'md:justify-center' : undefined}
            icon={<UtilityIcon d="M11.097 1.515a.75.75 0 01.589.882L10.666 7.5h4.47a.75.75 0 01.666 1.098l-5.697 9a.75.75 0 01-1.402-.47L9.753 12.5H5.284a.75.75 0 01-.666-1.098l5.697-9a.75.75 0 01.782-.387z" />}
            label={utilityLabel('Support')}
          />
          <NavItem
            size="sm"
            icon="bell"
            onClick={() => setShowNotifications((v) => !v)}
            title={c ? 'Notifications' : undefined}
            className={c ? 'md:justify-center' : undefined}
            label={utilityLabel('Notifications')}
          />
          <NavItem
            size="sm"
            onClick={() => setShowFeedback((v) => !v)}
            title={c ? 'Feedback' : undefined}
            className={c ? 'md:justify-center' : undefined}
            icon={<UtilityIcon d="M10 2c-1.716 0-3.408.106-5.07.31C3.806 2.45 3 3.414 3 4.517V17.25a.75.75 0 001.075.676l2.992-1.44a.75.75 0 01.348-.084h5.17c1.103 0 2.067-.806 2.207-1.93.106-.848.188-1.707.245-2.572a.249.249 0 00-.2-.259 4.5 4.5 0 01.157-8.857A41.127 41.127 0 0010 2z" />}
            label={utilityLabel('Feedback')}
          />
        </div>
      </Sidebar>

      {showFaq && <FaqModal audience={activeRole === 'admin' ? 'host' : activeRole} onClose={() => setShowFaq(false)} />}
      {showSupport && <SupportModal onClose={() => setShowSupport(false)} />}
      {showFeedback && <FeedbackPopover anchorRef={anchorRef} onClose={() => setShowFeedback(false)} />}
      {showNotifications && <NotificationsPopover anchorRef={anchorRef} onClose={() => setShowNotifications(false)} />}
    </>
  );
}
